import { memo, useMemo } from "react"
import { useURLSorterState } from "@/core/hooks"
import useCellMeasurer from "./useCellMeasurer"
import AutoSizer from "react-virtualized-auto-sizer"
import { VariableSizeList as List } from "react-window"
import { VTableContext } from "./TableContext"
import ItemRowHeaderRenderer from "./ItemRowHeaderRenderer"
import ItemRowRenderer from "./ItemRowRenderer"
import { StyledDivTable } from "./material"
import "twin.macro"
import { map, memoize, size, sum } from "lodash"
import { scaleToPercent, clsx, getNextSortState } from "@/utilities"
import { getRowKey } from "../Table/helper"
import { COL_SELECTION_WIDTH } from "./helper"

const memoCtxValue = memoize((props) => props)

export default memo(function VirtualizedTable({
    className,
    columns,
    dataSource,
    scrollY,
    isLoading,
    rowKey,
    rowSelection,
    expandable,
    itemCount,
    ...props
}) {
    const [sorter, setSorter] = useURLSorterState()

    const { listRef, ...methods } = useCellMeasurer()

    const handleSorting = (key) => setSorter(getNextSortState(sorter, key))

    const [tableWidth, colWidthAsPercents] = useMemo(() => {
        const isHaveCheckbox = typeof rowSelection?.onSelect === "function"
        const widths = map(columns, "width")

        if (isHaveCheckbox) {
            widths.unshift(COL_SELECTION_WIDTH)
        }

        const tableWidth = sum(widths)

        return [tableWidth, scaleToPercent(widths)]
    }, [columns, rowSelection?.onSelect])

    const _itemCount =
        typeof itemCount === "function"
            ? itemCount(dataSource, rowKey, expandable)
            : (itemCount ?? size(dataSource) + 1)

    return (
        <StyledDivTable
            className={clsx(className, "virtualized-table")}
            style={{ width: "100%", height: scrollY }}
        >
            <AutoSizer>
                {({ height, width }) => {
                    return (
                        <VTableContext.Provider
                            value={memoCtxValue({
                                columns: columns,
                                rowSelection,
                                isLoading,
                                sorter,
                                onSort: handleSorting,
                                tableWidth,
                                colWidthAsPercents,
                                expandable,
                                getRowKey: getRowKey(rowKey, isLoading),
                                ...methods,
                                ...props
                            })}
                        >
                            <List
                                ref={listRef}
                                height={height}
                                width={width}
                                itemCount={_itemCount}
                                itemSize={methods.getItemSize}
                                innerElementType={ItemRowHeaderRenderer}
                                tw="scrollbar-thin scrollbar-webkit"
                            >
                                {({ index, style }) => {
                                    const isHeaderRow = index === 0
                                    const itemIndex = index - 1
                                    const _getRowKey = getRowKey(rowKey, isLoading)

                                    if (isHeaderRow) {
                                        return null
                                    }

                                    return (
                                        <ItemRowRenderer
                                            {...{
                                                style: style,
                                                itemIndex: itemIndex,
                                                rowIndex: index,
                                                ...(!isHeaderRow && {
                                                    record: dataSource[itemIndex],
                                                    rowKey: _getRowKey(dataSource[itemIndex])
                                                })
                                            }}
                                        />
                                    )
                                }}
                            </List>
                        </VTableContext.Provider>
                    )
                }}
            </AutoSizer>
        </StyledDivTable>
    )
})
